import { Vue,Component,Prop,Watch} from 'vue-property-decorator';
import EmptyGraphMessage from '@/assets/common/AggregationFunction/EmptyGraphMessage/EmptyGraphMessage.vue';
import VerticalBarChart from '@/assets/common/VerticalBarChart/VerticalBarChart.vue';
import moment from "moment";

interface WorkerData {
    workerId: string;
    goal: number;
    performance: number;
    hasGoal: boolean;
    hasPerformance: boolean;
}

interface WorkerDataMap {
    [workerName: string]: WorkerData;
}

@Component({
    components: {
        EmptyGraphMessage,
        VerticalBarChart
    }
})

export default class ProductivityGraphModal extends Vue {

    @Prop() isShowProductivityGraphModal!: boolean
    private screen = "PRODUCTIVITYGRAPH";
    private agriFields = [] as any[];
    private crops = [] as any[];
    private plantings = [] as any[];
    private workItems = [] as any[];
    private workUnits = [] as any[];
    private selectedCropId: string | null = null;
    private selectedPlantingId: string | null = null;
    private selectedAgriFieldId : string | null = null;
    private selectedWorkItemId : string | null = null;
    private selectedWorkUnitId : string | null = null;
    private loading = false;
    private selectedStartDate: string | Date = new Date();
    private selectedEndDate: string | Date = new Date();
    private isButtonDisabled = false;
    private graphData: any = null
    private labels: any = null
    private chartData: any = null
    private options: any = null
    private hiddenWorkers: any = []
    private includeHiddenFlag = false
    private utilizedWorkUnitIds = [] as any[];

    @Watch('isShowProductivityGraphModal')
    private showPerformanceGraph() {
        if (this.isShowProductivityGraphModal) {
            this.loading = true;
            const currentDate = new Date();
            this.selectedStartDate = moment(currentDate).startOf('year').format('YYYY-MM-DD');
            this.selectedEndDate = moment(currentDate).endOf('year').format('YYYY-MM-DD');
            this.getCrops();
            this.getPlantings();
            this.getAgriFields(); 
            this.getWorkItems();
            this.getWorkUnits();
            this.getProductivityGraphData();
            this.loading = false;
        }
    }

    getProductivityGraphData() {
        this.$apiClient.doRequest('get', '/admin/productivity_graph').then(res => {
            this.selectedCropId = res.searchConditions.cropId
            this.selectedPlantingId = res.searchConditions.plantingId
            this.selectedAgriFieldId = res.searchConditions.agriFieldId
            this.selectedWorkItemId = res.searchConditions.workItemId
            this.selectedStartDate = res.searchConditions.displayFromDate ? res.searchConditions.displayFromDate : this.selectedStartDate
            this.selectedEndDate = res.searchConditions.displayToDate ? res.searchConditions.displayToDate : this.selectedEndDate
            this.includeHiddenFlag = res.searchConditions.includeHiddenFlag
            this.selectedWorkUnitId = res.searchConditions.workUnitId
            this.getUtilizedWorkUnits();
            if ((this.selectedStartDate > this.selectedEndDate) || (this.selectedStartDate === '' && this.selectedEndDate === '') || (this.selectedStartDate === '' && this.selectedEndDate !== '') 
                || (this.selectedStartDate !== '' && this.selectedEndDate === '')) {
                this.isButtonDisabled = true;
                this.graphData = [];
            } else {
                this.isButtonDisabled = false;
            }
            this.graphData = res.graphData;
            this.setGraphData();
            this.loading = false
        }).catch(error=>{
            this.$toasted.error('データの取得に失敗しました。')
            this.resetGraph();
            this.loading = false
        })
    }

    setGraphData () {
        const workerData: WorkerDataMap = {};
        this.graphData.forEach((item: { workerName: string; workerId: string; goal: number | null; performance: number | null; }) => {
            if (!workerData[item.workerName]) {
                workerData[item.workerName] = {
                    workerId: item.workerId,
                    goal: 0,
                    performance: 0,
                    hasGoal: false,
                    hasPerformance: false
                };
            }
            if (item.goal !== null) {
                workerData[item.workerName].goal = item.goal;
                workerData[item.workerName].hasGoal = true;
            }
            if (item.performance !== null) {
                workerData[item.workerName].performance = item.performance;
                workerData[item.workerName].hasPerformance = true;
            }
        });
        this.labels = Object.keys(workerData);
        const visibleLabels = this.labels.filter((name: string) => !this.hiddenWorkers.includes(name));
        const goalData = visibleLabels.map((name: string) => workerData[name].hasGoal ? workerData[name].goal : 0);
        const performanceData = visibleLabels.map((name: string) => workerData[name].hasPerformance ? workerData[name].performance : 0);
        this.chartData = {
            labels: visibleLabels,
            datasets: [
                {
                    type: 'bar',
                    label: '目標',
                    data: goalData,
                    borderColor: '#EDEF8F',
                    backgroundColor: '#EDEF8F',
                    borderRadius: 6,
                    categoryPercentage: 0.7,
                    barPercentage: 0.7,
                },
                {
                    type: 'bar',
                    label: '実績',
                    data: performanceData,
                    borderColor: '#7BD5FC',
                    backgroundColor: '#7BD5FC',
                    borderRadius: 6,
                    categoryPercentage: 0.7,
                    barPercentage: 0.7,
                },
            ],
        };  
        this.options = {
            responsive: true,
            maintainAspectRatio: false,
            legend: {
                align: 'end',
            },
            scales: {
                yAxes: [{
                    ticks: {
                        max: Math.ceil(Math.max(...goalData, ...performanceData) / 5) * 5,
                        beginAtZero: true,
                        stepSize: 5,
                    },
                    scaleLabel: {
                        display: this.graphData[0]?.workUnitName != null,
                        labelString: this.graphData[0]?.workUnitName != null ? `${this.graphData[0]?.workUnitName}／時間` : ''
                    },
                }],
            },
            layout: {
                padding: {
                    left: 20,
                    right: 20,
                    top: 5,
                    bottom: 0
                }
            },
        };
    }

    toggleData(label: string) {
        const i = this.hiddenWorkers.indexOf(label);
        if (i === -1) {
            this.hiddenWorkers.push(label);
        } else {
            this.hiddenWorkers.splice(i, 1);
        }
        this.setGraphData();
    }

    getCrops() {
        this.$apiClient.doRequest('get', '/crops/cropBytenentId').then(res => {
            this.crops = res.crops.sort((a: any, b: any) => a.name.localeCompare(b.name));
        });
    }
    
    getPlantings() {
        this.$apiClient.doRequest('get', '/plantings?with_hidden=true').then(res => {
            this.plantings = res.plantings.sort((a: any, b: any) => a.name.localeCompare(b.name));
        });
    }

    get filteredPlantings() {
        if (this.selectedCropId === null) {
            return this.includeHiddenFlag ? this.plantings : this.plantings.filter(p => this.selectedPlantingId == p.id || !p.is_hidden);
        } else {
            return this.includeHiddenFlag ? this.plantings.filter(p => p.crop.id === this.selectedCropId) : this.plantings.filter(p => this.selectedPlantingId == p.id || (!p.is_hidden && p.crop.id === this.selectedCropId));
        }
    }

    getAgriFields() {
        this.$apiClient.doRequest('get', '/agri_fields?with_hidden=true').then((res) => {
            this.agriFields = res.agri_fields
        })        
    }

    get filteredAgriFields() {
        return this.includeHiddenFlag ? this.agriFields : this.agriFields.filter(a => this.selectedAgriFieldId == a.id || !a.is_hidden);
    } 

    getWorkItems() {
        this.$apiClient.doRequest('get','/work_items?with_hidden=true').then((res) => {
            this.workItems = res.work_items
        })
    }

    get filteredWorkItems() {
        return this.includeHiddenFlag ? this.workItems : this.workItems.filter(wi => this.selectedWorkItemId == wi.id || !wi.is_hidden);
    }

    getWorkUnits() {
        this.$apiClient.doRequest('get', '/work_units?with_hidden=true').then(res => {
            this.workUnits = res.work_units
        })
    }

    get filteredWorkUnits() {
        return this.workUnits.filter(wu =>
            this.utilizedWorkUnitIds.includes(wu.id) && (!wu.is_hidden || this.includeHiddenFlag) || this.selectedWorkUnitId == wu.id
        );
    }

    handlePlantingSelect() {
        if (this.selectedPlantingId != null) {
            const selectedPlanting = this.plantings.find(p => p.id === this.selectedPlantingId);
            if (selectedPlanting) {
                this.selectedCropId = selectedPlanting.crop.id;
            }
        }
        this.getUtilizedWorkUnits();
    }    

    handleCropSelect() {
        this.selectedPlantingId = null
        this.getUtilizedWorkUnits();
    }

    closeModal() {
        this.$emit('update:isShowProductivityGraphModal', false);
        this.reset();
    }

    reset() {
        this.selectedAgriFieldId = null;
        this.selectedCropId = null;
        this.selectedPlantingId = null;
        this.selectedWorkItemId = null;
        this.selectedWorkUnitId = null;
        this.crops = [];
        this.plantings = [];
        this.agriFields = [];
        this.workItems = [];
        this.workUnits = [];
        this.isButtonDisabled = false;
        this.selectedStartDate = "";
        this.selectedEndDate = "";
        this.utilizedWorkUnitIds = []
        this.resetGraph();
    }

    resetGraph() {
        this.graphData = null;
        this.labels = null;
        this.chartData = null;
        this.options = null;
        this.hiddenWorkers = []
    }

    handleSearchClick() {
        this.loading = true;
        this.resetGraph();
        const params = {
            cropId: this.selectedCropId,
            plantingId: this.selectedPlantingId,
            agriFieldId: this.selectedAgriFieldId,
            selectedWorkId: this.selectedWorkItemId,
            startDate: this.selectedStartDate,
            endDate: this.selectedEndDate,
            includeHiddenFlag: this.includeHiddenFlag,
            screenName: this.screen,
            workUnitId: this.selectedWorkUnitId,
        }
        this.$apiClient.doRequest('post', `/graphScreenManagement`,params).then(res => {
            if (!this.isButtonDisabled) {
                this.getProductivityGraphData();
            } else {
                this.loading = false;
            }
        }).catch(error=>{
            this.$toasted.error('データの取得に失敗しました。')
            this.loading = false;
        })
    }

    validateDates() {
        if ((this.selectedStartDate > this.selectedEndDate) || (this.selectedStartDate === '' && this.selectedEndDate === '') || (this.selectedStartDate === '' && this.selectedEndDate !== '') 
            || (this.selectedStartDate !== '' && this.selectedEndDate === '')) {
            this.isButtonDisabled = true;
            this.graphData = [];
            return;
        } else {
            this.isButtonDisabled = false;
        }
    }
      
    dateNavigation(action: string) {
        this.validateDates();
        if (!this.isButtonDisabled) {
            const updateDate = (date: moment.MomentInput, offset: moment.DurationInputArg1, unit: moment.unitOfTime.Base) => moment(date).add(offset, unit).format('YYYY-MM-DD');
            const daysDifference = moment(this.selectedEndDate).diff(this.selectedStartDate, 'days')+1;
            const offset = (action === 'previous') ? -daysDifference : daysDifference;
            this.selectedStartDate = updateDate(this.selectedStartDate, offset, 'days');
            this.selectedEndDate = updateDate(this.selectedEndDate, offset, 'days');
        }
    }

    private getUtilizedWorkUnits() {
        this.utilizedWorkUnitIds = [];
        this.$apiClient.doRequest('get', `/admin/productivity_graph/utilized_work_units?crop_id=${this.selectedCropId || "null"}&&planting_id=${this.selectedPlantingId || "null"}&&agri_field_id=${this.selectedAgriFieldId || "null"}&&work_item_id=${this.selectedWorkItemId || "null"}`).then((res) => {
            this.utilizedWorkUnitIds = res.work_units.map((item: { id: any; }) => item.id);
            if (this.selectedWorkUnitId == null || !this.utilizedWorkUnitIds.includes(this.selectedWorkUnitId)) {
                this.selectedWorkUnitId = this.utilizedWorkUnitIds.length > 0 ? this.utilizedWorkUnitIds[0] : null;
            }
            this.filteredWorkUnits;
        });
    }
    
}